import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../db/engine';
import { useApp } from '../../context/AppContext';
import { BarChart3, FileText, Printer, Download, Search } from 'lucide-react';
import { fmtFecha } from '../../utils/fecha';
import { loadXLSX } from '../../utils/xlsx';

import { fmtMoneda as fmt$ } from '../../utils/moneda';
import { marcaImpresion } from '../../utils/marca';
import { imprimirHTML } from '../../utils/imprimir';


const fmtTipo    = t => (t === 'renovacion' ? 'Renovación' : 'Nueva');
const fmtEmision = t => (t === 'renovada' ? 'Renovación' : 'Nueva');
const itbmsDe    = r => (Number(r.prima) || 0) * (r.impuesto == null ? 5 : (Number(r.impuesto) || 0)) / 100;

// type: 'text' | 'money' | 'int' | 'date'
const fmtCell = (type, v) =>
  type === 'money' ? fmt$(v)
  : type === 'date' ? (v ? fmtFecha(v) : '')
  : type === 'int' ? String(v ?? '')
  : (v == null ? '' : String(v));

// ── Reporte 1: Producción de pólizas (lista) ──
const COLS_PRODUCCION = [
  ['Número',          r => r.numero || '',          'text'],
  ['Cliente',         r => r.cliente || '',          'text'],
  ['Aseguradora',     r => r.aseguradora || '',      'text'],
  ['Ramo',            r => r.ramo || '',             'text'],
  ['Tipo',            r => fmtTipo(r.tipo),          'text'],
  ['Emisión p/ aseg', r => fmtEmision(r.tipo_emision), 'text'],
  ['Prima',           r => Number(r.prima) || 0,     'money'],
  ['ITBMS',           r => itbmsDe(r),               'money'],
  ['Estado',          r => r.estado || '',           'text'],
  ['Vig. inicial',    r => r.fecha_inicio || '',     'date'],
  ['Vencimiento',     r => r.fecha_vencimiento || '', 'date'],
  ['Sobrecomisión',   r => r.sobrecomision_tipo === 'porcentaje' ? `${r.sobrecomision_valor}%`
                          : r.sobrecomision_tipo === 'monto' ? `$${Number(r.sobrecomision_valor||0).toFixed(2)}`
                          : '—', 'text'],
];

// ── Reporte 2: Producción por aseguradora (resumen) ──
const COLS_POR_ASEG = [
  ['Aseguradora',   g => g.aseguradora,  'text'],
  ['# Pólizas',     g => g.total,        'int'],
  ['# Nuevas',      g => g.nuevas,       'int'],
  ['# Renov.',      g => g.renov,        'int'],
  ['Prima total',   g => g.prima,        'money'],
  ['Prima nuevas',  g => g.primaNuevas,  'money'],
  ['Prima renov.',  g => g.primaRenov,   'money'],
];

const TITULOS = {
  produccion:   'Producción de Pólizas',
  por_aseg:     'Producción por Aseguradora',
};

export default function Reportes() {
  const { canView } = useApp();
  const [reporte, setReporte] = useState('produccion');
  const [aseguradoras, setAseguradoras] = useState([]);
  const [ramos, setRamos] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [rows, setRows] = useState(null); // null = aún no se generó
  const [loading, setLoading] = useState(false);

  const [filtros, setFiltros] = useState({
    fechaBase: 'fecha_emision', desde: '', hasta: '',
    aseguradora_id: '', ramo_id: '', estado: '', tipo: '', tipo_emision: '', agente_id: '',
  });
  const f = (k, v) => setFiltros(p => ({ ...p, [k]: v }));

  useEffect(() => {
    (async () => {
      const [a, r, ag] = await Promise.all([
        supabase.from('aseguradoras').select('id, nombre').eq('activo', true).order('nombre'),
        supabase.from('ramos').select('id, nombre').order('nombre'),
        supabase.from('agentes').select('id, nombre').eq('activo', true).order('nombre'),
      ]);
      setAseguradoras(a.data || []);
      setRamos(r.data || []);
      setAgentes(ag.data || []);
    })();
  }, []);

  const generar = useCallback(async () => {
    setLoading(true);
    try {
      const fb = filtros.fechaBase;
      let q = supabase.from('polizas')
        .select('numero, prima, impuesto, tipo, tipo_emision, estado, fecha_inicio, fecha_vencimiento, fecha_emision, agente_id, sobrecomision_tipo, sobrecomision_valor, clientes(nombre), aseguradoras(nombre), ramos(nombre)');
      if (filtros.aseguradora_id) q = q.eq('aseguradora_id', filtros.aseguradora_id);
      if (filtros.ramo_id)        q = q.eq('ramo_id', filtros.ramo_id);
      if (filtros.estado)         q = q.eq('estado', filtros.estado);
      if (filtros.tipo)           q = q.eq('tipo', filtros.tipo);
      if (filtros.tipo_emision)   q = q.eq('tipo_emision', filtros.tipo_emision);
      if (filtros.agente_id)      q = q.eq('agente_id', filtros.agente_id);
      if (filtros.desde)          q = q.gte(fb, filtros.desde);
      if (filtros.hasta)          q = q.lte(fb, filtros.hasta);
      q = q.order(fb, { ascending: false }).limit(5000);
      const { data } = await q;
      setRows((data || []).map(p => ({
        ...p,
        cliente: p.clientes?.nombre,
        aseguradora: p.aseguradoras?.nombre,
        ramo: p.ramos?.nombre,
      })));
    } catch (e) { alert('Error al generar el reporte: ' + (e.message || e)); }
    finally { setLoading(false); }
  }, [filtros]);

  // Agregado por aseguradora (reporte 2)
  const porAseg = useMemo(() => {
    if (!rows) return [];
    const m = {};
    rows.forEach(r => {
      const k = r.aseguradora || '— Sin aseguradora —';
      if (!m[k]) m[k] = { aseguradora: k, total: 0, prima: 0, nuevas: 0, renov: 0, primaNuevas: 0, primaRenov: 0 };
      const g = m[k];
      const prima = Number(r.prima) || 0;
      g.total++; g.prima += prima;
      if ((r.tipo_emision || 'nueva') === 'renovada') { g.renov++; g.primaRenov += prima; }
      else { g.nuevas++; g.primaNuevas += prima; }
    });
    return Object.values(m).sort((a, b) => b.prima - a.prima);
  }, [rows]);

  const fbLabel = filtros.fechaBase === 'fecha_inicio' ? 'Vig. inicial' : 'Fecha emisión';
  const resumenFiltro = useMemo(() => {
    const partes = [];
    if (filtros.desde || filtros.hasta) partes.push(`${fbLabel}: ${filtros.desde || '…'} a ${filtros.hasta || '…'}`);
    if (filtros.aseguradora_id) partes.push(`Aseguradora: ${aseguradoras.find(a => String(a.id) === String(filtros.aseguradora_id))?.nombre || ''}`);
    if (filtros.ramo_id)        partes.push(`Ramo: ${ramos.find(r => String(r.id) === String(filtros.ramo_id))?.nombre || ''}`);
    if (filtros.estado)         partes.push(`Estado: ${filtros.estado}`);
    if (filtros.tipo)           partes.push(`Tipo: ${fmtTipo(filtros.tipo)}`);
    if (filtros.tipo_emision)   partes.push(`Emisión p/ aseg: ${fmtEmision(filtros.tipo_emision)}`);
    if (filtros.agente_id)      partes.push(`Agente: ${agentes.find(a => String(a.id) === String(filtros.agente_id))?.nombre || ''}`);
    return partes.join('  ·  ') || 'Sin filtros';
  }, [filtros, fbLabel, aseguradoras, ramos, agentes]);

  // Config del reporte activo
  const activo = useMemo(() => {
    if (reporte === 'por_aseg') {
      const data = porAseg;
      const tot = data.reduce((s, g) => ({
        total: s.total + g.total, nuevas: s.nuevas + g.nuevas, renov: s.renov + g.renov,
        prima: s.prima + g.prima, primaNuevas: s.primaNuevas + g.primaNuevas, primaRenov: s.primaRenov + g.primaRenov,
      }), { total: 0, nuevas: 0, renov: 0, prima: 0, primaNuevas: 0, primaRenov: 0 });
      const footer = ['TOTAL', tot.total, tot.nuevas, tot.renov, tot.prima, tot.primaNuevas, tot.primaRenov];
      return { cols: COLS_POR_ASEG, data, footer };
    }
    const data = rows || [];
    const totPrima = data.reduce((s, r) => s + (Number(r.prima) || 0), 0);
    const totItbms = data.reduce((s, r) => s + itbmsDe(r), 0);
    // footer alineado a COLS_PRODUCCION (Prima en índice 6, ITBMS en 7)
    const footer = [`TOTAL (${data.length})`, '', '', '', '', '', totPrima, totItbms, '', '', '', ''];
    return { cols: COLS_PRODUCCION, data, footer };
  }, [reporte, rows, porAseg]);

  const exportarExcel = async () => {
    const M = marcaImpresion();
    const XLSX = await loadXLSX();
    const { cols, data, footer } = activo;
    const aoa = [
      [`${M.nombre} — ${TITULOS[reporte]}`],
      [resumenFiltro],
      [`${data.length} fila(s) · Generado: ${fmtFecha(new Date().toISOString().slice(0, 10))}`],
      [],
      cols.map(c => c[0]),
    ];
    data.forEach(row => {
      aoa.push(cols.map(([, acc, type]) => {
        const v = acc(row);
        return (type === 'money' || type === 'int') ? Number(v) || 0 : fmtCell(type, v);
      }));
    });
    aoa.push([]);
    aoa.push(footer.map((v, i) => {
      const type = cols[i]?.[2];
      if (v === '' || v == null) return '';
      return (type === 'money' || type === 'int') ? Number(v) || 0 : v;
    }));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = cols.map(() => ({ wch: 18 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
    XLSX.writeFile(wb, `${TITULOS[reporte].replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const imprimirPDF = () => {
    const M = marcaImpresion();
    const { cols, data, footer } = activo;
    const moneyIdx = cols.map(c => c[2] === 'money' || c[2] === 'int');
    const body = data.map(row => '<tr>' + cols.map(([, acc, type], i) =>
      `<td${moneyIdx[i] ? ' style="text-align:right"' : ''}>${fmtCell(type, acc(row))}</td>`
    ).join('') + '</tr>').join('');
    const footerHtml = '<tr class="total-row">' + footer.map((v, i) => {
      const type = cols[i]?.[2];
      const disp = (v === '' || v == null) ? '' : ((type === 'money' || type === 'int') ? fmtCell(type, v) : v);
      return `<td${moneyIdx[i] ? ' style="text-align:right"' : ''}>${disp}</td>`;
    }).join('') + '</tr>';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${TITULOS[reporte]}</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:11px;color:#111;margin:20px}
        .brand{display:flex;align-items:center;gap:10px;margin-bottom:12px;border-bottom:2px solid ${M.colores.steel};padding-bottom:10px}
        .brand-name{font-size:16px;font-weight:800;color:${M.colores.steel};letter-spacing:1px}
        .brand-tag{font-size:10px;color:${M.colores.sky}}
        h2{font-size:14px;margin:0 0 2px}
        .sub{font-size:11px;color:#555;margin-bottom:12px}
        table{width:100%;border-collapse:collapse;font-size:10px}
        th{background:${M.colores.steel};color:#fff;padding:6px 4px;text-align:left}
        td{padding:5px 4px;border-bottom:1px solid #e0e0e0}
        tr:nth-child(even) td{background:#f7f9fb}
        .total-row td{font-weight:700;border-top:2px solid ${M.colores.steel};background:#e8f0f8}
        @media print{body{margin:10px}}
      </style></head><body>
      <div class="brand">${M.logo}<div><div class="brand-name" style="text-transform:uppercase">${M.nombre}</div><div class="brand-tag">${M.tagline}</div></div></div>
      <h2>${TITULOS[reporte]}</h2>
      <div class="sub">${resumenFiltro} · ${data.length} fila(s) · Generado: ${fmtFecha(new Date().toISOString().slice(0, 10))}</div>
      <table>
        <thead><tr>${cols.map((c, i) => `<th${moneyIdx[i] ? ' style="text-align:right"' : ''}>${c[0]}</th>`).join('')}</tr></thead>
        <tbody>${body}${footerHtml}</tbody>
      </table>
    </body></html>`;
    imprimirHTML(html);
  };

  if (!canView('reportes')) {
    return <div style={{ padding: 40, color: 'var(--silver)' }}>No tienes acceso al módulo de Reportes.</div>;
  }

  const tieneDatos = rows !== null && activo.data.length > 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Reportes</div>
          <div className="page-subtitle">Producción de pólizas para reportería interna y hacia las aseguradoras.</div>
        </div>
      </div>

      {/* Selector de reporte */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        {[
          ['produccion', 'Producción de pólizas', FileText, 'Lista detallada filtrable, con totales.'],
          ['por_aseg', 'Producción por aseguradora', BarChart3, 'Resumen por compañía: # pólizas, prima, nuevas vs renovación.'],
        ].map(([key, label, Icon, desc]) => {
          const sel = reporte === key;
          return (
            <button key={key} onClick={() => setReporte(key)} style={{
              flex: '1 1 280px', textAlign: 'left', cursor: 'pointer',
              padding: '14px 16px', borderRadius: 10,
              background: sel ? 'linear-gradient(135deg, rgba(var(--sky-rgb),0.18), rgba(var(--sky-rgb),0.04))' : 'rgba(var(--steel-rgb),0.25)',
              border: `1px solid ${sel ? 'rgba(46,125,196,0.5)' : 'rgba(143,168,192,0.15)'}`,
              color: 'var(--text)', fontFamily: 'var(--font)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 4 }}>
                <Icon size={16} style={{ color: 'var(--sky)' }} /> {label}
              </div>
              <div style={{ fontSize: 12, color: 'var(--silver)' }}>{desc}</div>
            </button>
          );
        })}
      </div>

      {/* Filtros */}
      <div style={{ background: 'rgba(var(--steel-rgb),0.25)', borderRadius: 10, border: '1px solid rgba(var(--silver-rgb),0.15)', padding: 16, marginBottom: 18 }}>
        <div className="form-grid form-grid-4">
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Fecha base</label>
            <select className="form-select" value={filtros.fechaBase} onChange={e => f('fechaBase', e.target.value)}>
              <option value="fecha_emision">Fecha de emisión</option>
              <option value="fecha_inicio">Vigencia inicial</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Desde</label>
            <input className="form-input" type="date" value={filtros.desde} onChange={e => f('desde', e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Hasta</label>
            <input className="form-input" type="date" value={filtros.hasta} onChange={e => f('hasta', e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Aseguradora</label>
            <select className="form-select" value={filtros.aseguradora_id} onChange={e => f('aseguradora_id', e.target.value)}>
              <option value="">Todas</option>
              {aseguradoras.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Ramo</label>
            <select className="form-select" value={filtros.ramo_id} onChange={e => f('ramo_id', e.target.value)}>
              <option value="">Todos</option>
              {ramos.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Estado</label>
            <select className="form-select" value={filtros.estado} onChange={e => f('estado', e.target.value)}>
              <option value="">Todos</option>
              <option value="activa">Activa</option>
              <option value="vencida">Vencida</option>
              <option value="no_renovada">No renovada</option>
              <option value="renovada">Renovada</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Tipo interno</label>
            <select className="form-select" value={filtros.tipo} onChange={e => f('tipo', e.target.value)}>
              <option value="">Todos</option>
              <option value="nueva">Nueva</option>
              <option value="renovacion">Renovación</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Emisión p/ aseguradora</label>
            <select className="form-select" value={filtros.tipo_emision} onChange={e => f('tipo_emision', e.target.value)}>
              <option value="">Todas</option>
              <option value="nueva">Nueva</option>
              <option value="renovada">Renovación</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Agente</label>
            <select className="form-select" value={filtros.agente_id} onChange={e => f('agente_id', e.target.value)}>
              <option value="">Todos</option>
              {agentes.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={generar} disabled={loading}>
            <Search size={15} /> {loading ? 'Generando...' : 'Generar reporte'}
          </button>
        </div>
      </div>

      {/* Resultados */}
      {rows === null ? (
        <div style={{ textAlign: 'center', color: 'var(--silver)', padding: 40 }}>
          Configura los filtros y presiona <strong>Generar reporte</strong>.
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <div className="text-silver text-sm">{resumenFiltro} · <strong>{activo.data.length}</strong> fila(s)</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={imprimirPDF} disabled={!tieneDatos}><Printer size={14} /> Imprimir / PDF</button>
              <button className="btn btn-secondary btn-sm" onClick={exportarExcel} disabled={!tieneDatos}><Download size={14} /> Excel</button>
            </div>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr>{activo.cols.map((c) => (
                <th key={c[0]} style={(c[2] === 'money' || c[2] === 'int') ? { textAlign: 'right' } : undefined}>{c[0]}</th>
              ))}</tr></thead>
              <tbody>
                {activo.data.length === 0
                  ? <tr><td colSpan={activo.cols.length} style={{ textAlign: 'center', color: 'var(--silver)', padding: 20 }}>Sin resultados para esos filtros.</td></tr>
                  : activo.data.map((row, ri) => (
                    <tr key={ri}>
                      {activo.cols.map(([, acc, type], ci) => (
                        <td key={ci} style={(type === 'money' || type === 'int') ? { textAlign: 'right' } : undefined}>{fmtCell(type, acc(row))}</td>
                      ))}
                    </tr>
                  ))}
                {activo.data.length > 0 && (
                  <tr style={{ fontWeight: 700, background: 'rgba(var(--sky-rgb),0.1)' }}>
                    {activo.footer.map((v, i) => {
                      const type = activo.cols[i]?.[2];
                      const disp = (v === '' || v == null) ? '' : ((type === 'money' || type === 'int') ? fmtCell(type, v) : v);
                      return <td key={i} style={(type === 'money' || type === 'int') ? { textAlign: 'right' } : undefined}>{disp}</td>;
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
